import { useCallback, useEffect, useState } from "react";
import { Target, Plus, Trash2, ArrowDownToLine } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { Button } from "../ui/button";
import { Input, Textarea } from "../ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_DEVELOPMENT_ACTIONS, APPRAISAL_DEVELOPMENT_CARRY_FORWARD } from "../../constants/api";

const STATUS_META = {
    not_started: { label: "Not started", className: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200" },
    in_progress: { label: "In progress", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
    completed: { label: "Completed", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
    dropped: { label: "Dropped", className: "bg-slate-200 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300" },
};

const EMPTY = { area: "", action: "", support_needed: "", suggested_training: "", target_date: null, goal_id: null };

/**
 * The development plan: what the employee will do about the review.
 *
 * The important behaviour here is what stays usable after publish. Ratings
 * freeze; these actions do not, because they are worked on for months
 * afterwards. Once published the wording is read-only but progress is not,
 * which is exactly what the API enforces.
 *
 * `role` is "employee" or "rm"; anything else (HR) reads only.
 */
const DevelopmentPlanPanel = ({ appraisal, goals = [], role }) => {
    const { fetch, create, update, remove } = useApiMethods();
    const [rows, setRows] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [picked, setPicked] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(EMPTY);

    const appraisalId = appraisal?.id;
    const published = !!appraisal?.published_at;
    const isEmployee = role === "employee";
    const isRM = role === "rm";

    const load = useCallback(async () => {
        if (!appraisalId) return;
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_DEVELOPMENT_ACTIONS, { appraisal_id: appraisalId });
            setRows(res?.data || []);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appraisalId]);

    const loadSuggestions = useCallback(async () => {
        if (!appraisalId || !isRM || published) return;
        try {
            const res = await fetch(APPRAISAL_DEVELOPMENT_CARRY_FORWARD, { appraisal_id: appraisalId });
            setSuggestions(res?.data || []);
        } catch (_) {
            setSuggestions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appraisalId, isRM, published]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

    const addAction = async () => {
        if (!form.area.trim() || !form.action.trim()) {
            ToastMsg("error", "Give the area a name and say what will be done");
            return;
        }
        setSaving(true);
        try {
            const res = await create(APPRAISAL_DEVELOPMENT_ACTIONS, {
                appraisal_id: appraisalId, ...form,
                target_date: form.target_date || null,
                goal_id: form.goal_id || null,
            });
            if (res?.status === "success") {
                ToastMsg("success", "Development action added");
                setForm(EMPTY); setAdding(false);
                await load();
            } else {
                ToastMsg("error", res?.message || "Could not add the action");
            }
        } catch (_) {
            ToastMsg("error", "Could not add the action");
        } finally {
            setSaving(false);
        }
    };

    const patch = async (row, body, quiet) => {
        try {
            const res = await update(APPRAISAL_DEVELOPMENT_ACTIONS, { id: row.id, ...body });
            if (res?.status === "success") {
                if (!quiet) ToastMsg("success", "Updated");
                setRows((rs) => rs.map((r) => (r.id === row.id ? res.data : r)));
            } else {
                ToastMsg("error", res?.message || "Could not update");
                await load();
            }
        } catch (_) {
            ToastMsg("error", "Could not update");
        }
    };

    const removeAction = async (row) => {
        try {
            const res = await remove(`${APPRAISAL_DEVELOPMENT_ACTIONS}?id=${row.id}`);
            if (res?.status === "success") {
                setRows((rs) => rs.filter((r) => r.id !== row.id));
            } else {
                ToastMsg("error", res?.message || "Could not remove");
            }
        } catch (_) {
            ToastMsg("error", "Could not remove");
        }
    };

    const carryForward = async () => {
        if (!picked.length) return;
        setSaving(true);
        try {
            const res = await create(APPRAISAL_DEVELOPMENT_CARRY_FORWARD, {
                appraisal_id: appraisalId, action_ids: picked,
            });
            if (res?.status === "success") {
                ToastMsg("success", "Carried forward");
                setPicked([]);
                setRows(res.data || []);
                await loadSuggestions();
            } else {
                ToastMsg("error", res?.message || "Could not carry those forward");
            }
        } catch (_) {
            ToastMsg("error", "Could not carry those forward");
        } finally {
            setSaving(false);
        }
    };

    const canAdd = (isEmployee || isRM) && !published;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <h3 className="text-base font-semibold flex items-center gap-2">
                    <Target className="w-4 h-4" /> Development plan
                </h3>
                {canAdd && !adding && (
                    <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add action
                    </Button>
                )}
            </div>
            <p className="text-xs text-muted-foreground mb-3">
                {published
                    ? "These were agreed at the review. The wording is fixed now, but progress stays up to date here."
                    : "What this person will work on next, and what they need to do it."}
            </p>

            {/* Carry-forward. Suggested, never automatic: an action nobody
                re-confirms is usually one that stopped mattering. */}
            {isRM && !published && suggestions.length > 0 && (
                <div className="rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-3 mb-3">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                        Still open from earlier cycles
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 mb-2">
                        Pick the ones that still apply. Progress carries across; the target date is set fresh.
                    </p>
                    <div className="space-y-1">
                        {suggestions.map((s) => (
                            <label key={s.id} className="flex items-start gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={picked.includes(s.id)}
                                    onChange={(e) => setPicked((p) =>
                                        e.target.checked ? [...p, s.id] : p.filter((x) => x !== s.id))}
                                />
                                <span>
                                    <span className="font-medium">{s.area}</span>
                                    <span className="text-xs text-muted-foreground">
                                        {s.carried_from_cycle ? ` · from ${s.carried_from_cycle}` : ""}
                                        {` · ${STATUS_META[s.status]?.label || s.status}`}
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                    <Button size="sm" variant="outline" className="mt-2"
                        disabled={saving || !picked.length} onClick={carryForward}>
                        <ArrowDownToLine className="w-4 h-4 mr-1" /> Carry {picked.length || ""} forward
                    </Button>
                </div>
            )}

            {adding && (
                <div className="border border-border rounded-lg p-3 mb-3 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                            <label className="block text-xs font-medium mb-1">Development area</label>
                            <Input placeholder="e.g. Clinical documentation"
                                value={form.area}
                                onChange={(e) => setForm({ ...form, area: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1">Against a KPI (optional)</label>
                            <Select allowClear placeholder="General"
                                value={form.goal_id}
                                onChange={(v) => setForm({ ...form, goal_id: v ?? null })}
                                options={goals.map((g) => ({ value: g.id, label: g.kpi_name }))}
                                className="w-full" />
                        </div>
                    </div>
                    <Textarea rows={2} placeholder="What will they actually do?"
                        value={form.action}
                        onChange={(e) => setForm({ ...form, action: e.target.value })} />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div>
                            <label className="block text-xs font-medium mb-1">Support needed</label>
                            <Input placeholder="e.g. two hours a month"
                                value={form.support_needed}
                                onChange={(e) => setForm({ ...form, support_needed: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1">Suggested training</label>
                            <Input placeholder="Course or session name"
                                value={form.suggested_training}
                                onChange={(e) => setForm({ ...form, suggested_training: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1">Target date</label>
                            <DatePicker className="w-full h-9" format="DD-MM-YYYY"
                                value={form.target_date ? dayjs(form.target_date) : null}
                                onChange={(d) => setForm({ ...form, target_date: d ? d.format("YYYY-MM-DD") : null })} />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" variant="brand" disabled={saving} onClick={addAction}>Save action</Button>
                        <Button size="sm" variant="ghost" disabled={saving}
                            onClick={() => { setAdding(false); setForm(EMPTY); }}>Cancel</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-6 text-muted-foreground text-sm">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                    No development actions on this appraisal.
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => {
                        const meta = STATUS_META[r.status] || STATUS_META.not_started;
                        const overdue = r.target_date
                            && ["not_started", "in_progress"].includes(r.status)
                            && dayjs(r.target_date).isBefore(dayjs(), "day");
                        return (
                            <div key={r.id} className="border border-border rounded-lg p-3 space-y-2">
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                    <div className="min-w-[200px] flex-1">
                                        <div className="font-medium text-sm">{r.area}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {r.goal_name ? `${r.goal_name} · ` : ""}
                                            {r.source === "carried_forward"
                                                ? `carried from ${r.carried_from_cycle || "an earlier cycle"}`
                                                : r.source === "employee" ? "proposed by the employee" : "set by the manager"}
                                            {r.target_date && ` · due ${dayjs(r.target_date).format("DD-MM-YYYY")}`}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {overdue && (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                                                Past date
                                            </span>
                                        )}
                                        <span className={cn("text-xs px-2 py-0.5 rounded-full", meta.className)}>
                                            {meta.label}
                                        </span>
                                        {!published && (isRM || (isEmployee && r.source === "employee")) && (
                                            <button type="button" className="text-red-500 hover:text-red-600"
                                                onClick={() => removeAction(r)}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <p className="text-sm">{r.action}</p>
                                {r.support_needed && (
                                    <p className="text-xs text-muted-foreground">
                                        <span className="font-medium">Support: </span>{r.support_needed}
                                    </p>
                                )}
                                {r.suggested_training && (
                                    <p className="text-xs text-muted-foreground">
                                        <span className="font-medium">Training: </span>{r.suggested_training}
                                    </p>
                                )}

                                {/* Progress is the employee's, before and after
                                    publish: they do the work, so they say how far
                                    along it is. */}
                                {isEmployee ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">Status</label>
                                            <Select
                                                value={r.status}
                                                onChange={(v) => patch(r, { status: v })}
                                                className="w-full"
                                                options={Object.entries(STATUS_META)
                                                    .map(([value, m]) => ({ value, label: m.label }))}
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="block text-xs font-medium mb-1">Progress note</label>
                                            <Input
                                                defaultValue={r.progress_note || ""}
                                                placeholder="Where you have got to"
                                                onBlur={(e) => {
                                                    const v = e.target.value.trim();
                                                    if (v !== (r.progress_note || "")) patch(r, { progress_note: v }, true);
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : r.progress_note ? (
                                    <p className="text-xs text-muted-foreground">
                                        <span className="font-medium">Progress: </span>{r.progress_note}
                                    </p>
                                ) : null}

                                {isRM ? (
                                    <div>
                                        <label className="block text-xs font-medium mb-1">Your comment</label>
                                        <Input
                                            defaultValue={r.rm_comment || ""}
                                            placeholder="Feedback on progress"
                                            onBlur={(e) => {
                                                const v = e.target.value.trim();
                                                if (v !== (r.rm_comment || "")) patch(r, { rm_comment: v }, true);
                                            }}
                                        />
                                    </div>
                                ) : r.rm_comment ? (
                                    <p className="text-xs text-muted-foreground">
                                        <span className="font-medium">Manager: </span>{r.rm_comment}
                                    </p>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DevelopmentPlanPanel;
